import {
    AutoComplete,
    CommandFilter,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Option,
    RenderOptionType,
} from "@/shared/components";
import type { FieldPath, UseFormReturn } from "react-hook-form";

interface FormFieldAutocompleteProps<T extends Record<string, unknown>, D extends Record<string, unknown>> {
    form: UseFormReturn<T>;
    label?: string;
    placeholder?: string;
    name: FieldPath<T>;
    options: Option<D>[];
    showSearchIcon?: boolean;
    allowCustomValue?: boolean;
    renderOption?: RenderOptionType<D>;
    className?: string;
    disableBuiltInFilter?: boolean;
    rankFn?: CommandFilter;
    readOnly?: boolean;
    disabled?: boolean;
    onChange?: (value: string) => void;
    "data-testid"?: string;
}

export function FormFieldAutocomplete<T extends Record<string, unknown>, D extends Record<string, unknown>>({
    form,
    label,
    placeholder = "Zoeken",
    showSearchIcon = true,
    allowCustomValue = false,
    disabled,
    name,
    options,
    renderOption,
    className,
    disableBuiltInFilter = false,
    rankFn,
    onChange,
    "data-testid": dataTestid,
}: Readonly<FormFieldAutocompleteProps<T, D>>) {
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem className={className}>
                    {label && <FormLabel>{label}</FormLabel>}
                    <FormControl>
                        <AutoComplete
                            placeholder={placeholder}
                            emptyMessage="Geen resultaten."
                            showSearchIcon={showSearchIcon}
                            allowCustomValue={allowCustomValue}
                            options={options}
                            renderOption={renderOption}
                            value={field.value as string}
                            onChange={(value) => {
                                field.onChange(value);
                                if (onChange) {
                                    onChange(value);
                                }
                            }}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            disableBuiltInFilter={disableBuiltInFilter}
                            rankFn={rankFn}
                            hasError={!!form.formState.errors[name]}
                            data-testid={dataTestid}
                            disabled={disabled}
                        />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
